import type {
	OrderDetailResponseDto,
	OrderPackage,
	OrderSetupDetailResponseDto,
} from "Infrastructure/Api/Api";
import { getFormValuesDiff } from "Infrastructure/Utilities/Forms/Helpers/getFormValuesDiff";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useUpdateOrder } from "Order/Api/Mutation/updateOrderMutation";
import {
	getOrderEditSchema,
	type OrderEditModel,
} from "Order/Components/Form/Schema/orderEditSchema";
import {
	getOrderB2BPromoFormData,
	getOrderFieldsFormData,
	getOrderGeneralFormData,
	getOrderStatusFormData,
} from "Order/Utilities/getFormData";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TOptions } from "i18next";
import { useEffect } from "react";
import { type Resolver, useForm } from "react-hook-form";
import type { ObjPathProxy } from "ts-object-path";

type OrderDurationResponse = {
	jobDurationDays?: number | null;
};

export const useOrderEditForm = (
	order: OrderDetailResponseDto,
	t: (
		resourcePath: ObjPathProxy<unknown, string>,
		options?: TOptions,
	) => string,
	orderSetup?: OrderSetupDetailResponseDto,
) => {
	const jobDurationDays = (
		order as OrderDetailResponseDto & OrderDurationResponse
	).jobDurationDays;
	const jobDurationDaysDefault: OrderEditModel["jobDurationDays"] =
		jobDurationDays ?? "";
	const packageTypeDefault: OrderEditModel["packageType"] =
		order.packageType ?? "";
	const formDefaultValues = {
		dateNextAction: order.dateNextAction ?? "",
		jobDurationDays: jobDurationDaysDefault,
		partnerId: order.partner?.id ?? null,
		userId: order.userId,
		packageType: packageTypeDefault,
		...getOrderGeneralFormData(order),
		...getOrderStatusFormData(order),
		...getOrderB2BPromoFormData(order),
		fields: getOrderFieldsFormData(order, orderSetup),
	};

	const form = useForm<OrderEditModel>({
		resolver: zodResolver(
			getOrderEditSchema(t, orderSetup),
		) as Resolver<OrderEditModel>,
		defaultValues: formDefaultValues,
	});

	const {
		watch,
		reset,
		formState: { dirtyFields },
		setValue,
	} = form;
	const statusValue = watch("status");

	const { mutate: updateOrder, isPending } = useUpdateOrder(order.id);
	const { handleServerError } = useHandleServerError(form);

	useEffect(() => {
		reset(formDefaultValues);
	}, [orderSetup, order]);

	useEffect(() => {
		const defaultValues = getOrderStatusFormData(order);

		setValue("dateStarting", defaultValues.dateStarting, {
			shouldDirty: true,
		});

		// Preserve existing dateDone if status is already 'done' and dateDone exists
		if (statusValue === "done") {
			// Only reset dateDone if the order status wasn't already 'done' or if no dateDone exists
			if (order.status !== "done" || !order.dateDone) {
				setValue("dateDone", "", { shouldDirty: true });
			} else {
				// Preserve existing dateDone when status is already 'done' and dateDone exists
				setValue("dateDone", defaultValues.dateDone, { shouldDirty: false });
			}
		} else {
			setValue("dateDone", defaultValues.dateDone, { shouldDirty: true });
		}

		setValue("cancellationReason", defaultValues.cancellationReason, {
			shouldDirty: true,
		});
	}, [statusValue]);

	const handleSubmitUpdate = () => {
		handleServerError((data) => {
			const changedValues = getFormValuesDiff(
				dirtyFields,
				data,
			) as Partial<OrderEditModel>;
			const updatePayload = {
				...changedValues,
				...(changedValues.jobDurationDays === ""
					? { jobDurationDays: null }
					: {}),
				...(changedValues.packageType === "" ? { packageType: null } : {}),
			} as Omit<
				Partial<OrderEditModel>,
				"jobDurationDays" | "fields" | "packageType"
			> & {
				jobDurationDays?: number | null;
				packageType?: OrderPackage | null;
			};

			const fields =
				(data.fields?.length ?? 0) > 0
					? data.fields?.map((field) => ({
							orderFieldId: field.orderFieldId,
							value: field.value !== null ? String(field.value) : null,
						}))
					: undefined;

			updateOrder({
				...updatePayload,
				fields: fields,
			});
		});
	};

	return { form, handleSubmitUpdate, isPending, statusValue };
};
